import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Hash } from "lucide-react";
import {
  referencesApi,
  type ResolvedReference,
} from "../../api/references.api";
import { referenceLabels } from "../references/reference-catalog";
import {
  type InlineReference,
  makeToken,
} from "../messaging/inline-references";
import "../messaging/inline-composer.css";
export function InlineComposer({
  text,
  refs,
  onChange,
  scope,
  disabled,
  attachment,
  sendAction,
  label = "Votre message",
  submitOnEnter = true,
  maxReferences = 50,
  maxLength = 20000,
  placeholder,
  mentionPeople = false,
}: {
  placeholder?: string;
  mentionPeople?: boolean;
  maxReferences?: number;
  maxLength?: number;
  label?: string;
  submitOnEnter?: boolean;
  text: string;
  refs: InlineReference[];
  onChange: (text: string, refs: InlineReference[]) => void;
  scope: string[];
  disabled: boolean;
  attachment?: ReactNode;
  sendAction?: ReactNode;
}) {
  const helpId = useId();
  const input = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(text.length),
    [dismissed, setDismissed] = useState(false),
    [selected, setSelected] = useState(0);
  const match = text.slice(0, caret).match(mentionPeople ? /(?:^|\s)[#@]([^#@[\]\n]{0,100})$/ : /(?:^|\s)#([^#[\]\n]{0,100})$/);
  const peopleOnly = mentionPeople && match?.[0].trimStart().startsWith('@');
  const term = match?.[1] ?? "";
  const active = !!match && !dismissed && !disabled;
  const [search, setSearch] = useState("");
  useEffect(() => {
    setSearch("");
    setSelected(0);
    const t = setTimeout(() => setSearch(term.trim()), 350);
    return () => clearTimeout(t);
  }, [term]);
  const query = useQuery({
    queryKey: ["message-inline-search", ...scope, search, !!peopleOnly],
    enabled: active && search.length >= 2 && search === term.trim(),
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      const results = await Promise.allSettled(
        (
          Object.keys(referenceLabels) as Array<InlineReference["entityType"]>
        ).filter(type => !peopleOnly || ['CANDIDATE', 'INSTRUCTOR', 'SECRETARY'].includes(type)).map((type) => referencesApi.search(search, type)),
      );
      return {
        partial: results.some((r) => r.status === "rejected"),
        items: results
          .flatMap((r) =>
            r.status === "fulfilled"
              ? r.value.items.filter((x) => x.available)
              : [],
          )
          .slice(0, 30),
      };
    },
  });
  const items =
    search === term.trim() && !query.isFetching
      ? (query.data?.items ?? [])
      : [];
  function add(r: ResolvedReference) {
    if (!r.available || !match || disabled || refs.length >= maxReferences)
      return;
    const token = makeToken(r.label, refs),
      start = caret - term.length - 1;
    const next = text.slice(0, start) + token + " " + text.slice(caret);
    onChange(next, [
      ...refs,
      { entityType: r.entityType, entityId: r.entityId, token },
    ]);
    setDismissed(true);
    requestAnimationFrame(() => {
      input.current?.focus();
      input.current?.setSelectionRange(
        start + token.length + 1,
        start + token.length + 1,
      );
      setCaret(start + token.length + 1);
    });
  }
  return (
    <div className="inline-composer">
      <label>
        <span className="messaging-sr-only">{label}</span>
        <textarea
          ref={input}
          rows={submitOnEnter ? 3 : 10}
          aria-label={label}
          value={text}
          maxLength={maxLength}
          disabled={disabled}
          placeholder={
            placeholder ?? (submitOnEnter
              ? "Rédigez un message…"
              : "Rédigez votre note… # pour lier un objet")
          }
          aria-describedby={helpId}
          onChange={(e) => {
            onChange(
              e.target.value,
              refs.filter((r) => e.target.value.includes(r.token)),
            );
            setCaret(e.target.selectionStart);
            setDismissed(false);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              (submitOnEnter || (active && items.length > 0))
            ) {
              e.preventDefault();
              if (e.repeat) return;
              if (active) {
                if (items.length) add(items[selected % items.length]);
                return;
              }
              const form = e.currentTarget.form;
              const submit = form?.querySelector<HTMLButtonElement>(
                'button[type="submit"]',
              );
              if (submit && !submit.disabled) form?.requestSubmit(submit);
              return;
            }
            if (!active) return;
            if (e.key === "Escape") {
              e.preventDefault();
              setDismissed(true);
            }
            if (
              items.length &&
              (e.key === "ArrowDown" || e.key === "ArrowUp")
            ) {
              e.preventDefault();
              setSelected(
                (selected + (e.key === "ArrowDown" ? 1 : items.length - 1)) %
                  items.length,
              );
            }
          }}
        />
      </label>
      <div className="inline-composer-tools">
        {attachment}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          title="Insérer un objet métier avec #"
          aria-label="Joindre un objet métier"
          disabled={disabled || refs.length >= maxReferences}
          onClick={() => {
            const pos = input.current?.selectionStart ?? text.length;
            onChange(text.slice(0, pos) + " #" + text.slice(pos), refs);
            setCaret(pos + 2);
            setDismissed(false);
            requestAnimationFrame(() => {
              input.current?.focus();
              input.current?.setSelectionRange(pos + 2, pos + 2);
            });
          }}
        >
          <Hash size={17} aria-hidden="true" />
        </button>
        <span className="inline-send-action">{sendAction}</span>
        <small id={helpId}>
          {submitOnEnter
            ? "Entrée pour envoyer · Maj + Entrée pour une ligne · "
            : ""}
          {mentionPeople ? '@ pour une personne · # pour un objet' : '# pour lier un objet'}
        </small>
      </div>
      {active && (
        <section
          className="inline-suggestions"
          aria-label="Suggestions d’objets métier"
        >
          {term.trim().length < 2 ? (
            <p>Saisissez au moins 2 caractères après {peopleOnly ? '@' : '#'}.</p>
          ) : query.isFetching || search !== term.trim() ? (
            <p role="status">Recherche…</p>
          ) : (
            <>
              {items.map(
                (r, i) =>
                  r.available && (
                    <button
                      key={r.entityType + r.entityId}
                      type="button"
                      className={i === selected ? "is-active" : ""}
                      disabled={refs.length >= maxReferences}
                      onClick={() => add(r)}
                    >
                      <strong>{r.label}</strong>
                      <small>{referenceLabels[r.entityType]}</small>
                    </button>
                  ),
              )}
              {!items.length && (
                <p>Aucun objet accessible pour cette recherche.</p>
              )}
              {query.data?.partial && (
                <p role="status">
                  Certaines recherches sont indisponibles. Réessayez.
                </p>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
